import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
// bootstrap css
import 'bootstrap/dist/css/bootstrap.min.css';
import { datadogRum } from '@datadog/browser-rum';
import logger from 'Services/MyTuiProfileCustomLogger';

import TppSwitcher from 'Layout/Components/TppSwitcher';
import MaintenanceBanner from 'Components/MaintenanceBanner';
import { getEnvironmentVariablesHandler } from 'Handlers/EnvHandler';
import { useMsal } from '@azure/msal-react';
import { jwtDecode } from 'jwt-decode';
import Layout from 'Layout/Layout';
import {
  getCountries,
  getDestinations,
  getJobFamilies,
  getJobTitles,
  getKeyWords,
  getLanguages,
} from 'Store/Actions/MasterData/MasterDataAction';
import { getUser } from 'Store/Actions/User/UserAction';
import { getTppSettings } from 'Store/Actions/Setting/SettingAction';
import { getDatadogClientToken } from 'Constants/DatadogConstants';

const LoadingSpinner = () => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
      fontSize: '1.2rem',
    }}
  >
    <div>
      <i className="bi bi-arrow-clockwise bi-spin" style={{ fontSize: '2rem', marginRight: '10px' }} />
      Loading My Tui Profile Web...
    </div>
  </div>
);

/**
 * Sanitizes version string for Datadog tagging requirements
 * @param {string} version - The version string to sanitize
 * @returns {string} - Sanitized version string
 */
const sanitizeVersion = (version) => {
  if (!version) return 'unknown';

  // Datadog tag requirements: can contain alphanumerics, underscores, minuses, colons, periods, slashes
  // Must start with a letter, otherwise prepend 'v'
  let sanitized = version.toString().trim();

  // If version doesn't start with a letter, prepend 'v'
  if (!/^[a-zA-Z]/.test(sanitized)) {
    sanitized = 'v' + sanitized;
  }

  // Replace any invalid characters with underscores
  sanitized = sanitized.replace(/[^a-zA-Z0-9_\-:./]/g, '_');

  return sanitized.toLowerCase();
};

const App = ({ isAdmin, maintenanceActive, maintenanceStart, maintenanceEnd }) => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(true);
  const { accounts } = useMsal();

  useEffect(() => {
    const initializeApp = async () => {
      try {
        // Initialize all required data
        await Promise.all([
          dispatch(getJobFamilies()),
          dispatch(getJobTitles()),
          dispatch(getCountries()),
          dispatch(getDestinations()),
          dispatch(getTppSettings()),
          dispatch(getKeyWords()),
          dispatch(getLanguages()),
          dispatch(getUser()),
        ]);

        // Initialize Datadog
        const account = accounts[0];
        const profile = account?.idTokenClaims || jwtDecode(account?.idToken);
        const envVariables = getEnvironmentVariablesHandler();
        const sanitizedVersion = sanitizeVersion(envVariables.version);

        datadogRum.init({
          applicationId: '574208f0-7c93-4e3a-8905-e48479bf96df',
          clientToken: getDatadogClientToken(),
          site: 'datadoghq.eu',
          service: 'my-tui-profile',
          env: envVariables.environment,
          version: sanitizedVersion, // Sanitized version
          sessionSampleRate: 100,
          sessionReplaySampleRate: 100,
          trackUserInteractions: true,
          trackResources: true,
          trackLongTasks: true,
          defaultPrivacyLevel: 'allow',
        });

        datadogRum.setUser({
          email: profile.email,
          name: profile.given_name,
        });
      } catch (error) {
        logger.error('Error initializing app:', error);
      } finally {
        setIsLoading(false);
      }
    };

    initializeApp();
  }, [dispatch, accounts]);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <>
      {isAdmin && maintenanceActive && <MaintenanceBanner startDate={maintenanceStart} endDate={maintenanceEnd} />}
      <Layout style={isAdmin && maintenanceActive ? { marginTop: '60px' } : {}}>
        <TppSwitcher />
      </Layout>
    </>
  );
};

App.propTypes = {
  isAdmin: PropTypes.bool,
  maintenanceActive: PropTypes.bool,
  maintenanceStart: PropTypes.string,
  maintenanceEnd: PropTypes.string,
};

export default App;
